<template>
  <div
    v-tippy="tooltip"
    class="chip flex gap-1"
    :class="[
      'chip-' + severity,
      {
        'chip-rounded': rounded,
        'chip-outlined': outlined,
        'chip-no-background': noBackground,
        'chip-plain': plain,
        'chip-dot': dot
      },
      'chip-' + size
    ]"
    :style="color ? { color: color, backgroundColor: addOpacityToHexColor(color, 0.1) } : {}"
  >
    <Icon
      v-if="icon != null && icon != ''"
      :type="icon"
      class="text-xs mr-1"
      :style="{ fontSize: 'inherit!important' }"
    />
    <Icon
      v-if="dot"
      type="circle"
      class="mr-1"
      :class="['text-[6px]']"
    />
    <Avatar
      v-if="avatar || avatarText"
      :url="avatar"
      :text="avatarText"
      :class="avatarClasses"
    ></Avatar>
    <slot />
    {{ label }}
    <Icon
      v-if="canRemove"
      type="xmark"
      style="margin-inline-end: -3px"
      class="cursor-pointer text-xs text-quaternary pl-1 mt-0.5"
      @click="removeChip"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'

const props = defineProps({
  label: {
    type: String,
    default: null
  },
  color: {
    type: String,
    default: null
  },
  severity: {
    type: String,
    default: 'default'
  },
  rounded: {
    type: Boolean,
    default: true
  },
  outlined: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md'
  },
  icon: {
    type: String,
    default: null
  },
  avatar: {
    type: String,
    default: null
  },
  avatarText: {
    type: String,
    default: null
  },
  canRemove: {
    type: Boolean,
    default: false
  },
  noBackground: {
    type: Boolean,
    default: false
  },
  dot: {
    type: Boolean,
    default: false
  },
  plain: {
    type: Boolean,
    default: false
  },
  tooltip: {
    type: String,
    default: null
  }
})
const emit = defineEmits(['removeChip'])

const avatarClasses = computed(() => {
  const sizeClasses = {
    xs: '-ml-1 mr-0.5 !w-4 !h-4',
    sm: '-ml-[6px] mr-1 !w-5 !h-5',
    md: '-ml-[10px] mr-1.5 !w-6 !h-6',
    lg: '-ml-[10px] mr-2 !w-7 !h-7'
  }
  return sizeClasses[props.size as keyof typeof sizeClasses] || sizeClasses.md
})

function removeChip(event: MouseEvent) {
  if (event) {
    emit('removeChip', event)
  } else {
    emit('removeChip')
  }
}

function addOpacityToHexColor(hex: string, opacity: number) {
  hex = hex.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  return `rgba(${r},${g},${b},${opacity})`
}
</script>

<style>
.chip {
  align-items: center;
  display: inline-flex;
  font-weight: var(--font-medium);
  max-width: 100%;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  white-space: nowrap;
  vertical-align: middle;
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  border-style: solid;
  border-width: 1;
  border: 1px solid;
  border-radius: var(--rounded-md);
  box-sizing: content-box;
}

.chip-xs {
  font-size: var(--text-2xs);
  padding: 0 var(--s-1-5);
}
.chip-sm {
  font-size: var(--text-xs);
  padding: var(--s-05) var(--s-2);
}
.chip-md {
  font-size: var(--text-sm);
  padding: var(--s-05) var(--s-3);
}
.chip-lg {
  font-size: var(--text-md);
  padding: 0 var(--s-3);
}

.chip-rounded {
  border-radius: var(--rounded-full);
}

.chip-outlined {
  background: transparent !important;
}

.chip-outlined:not(.chip-label) {
  border-width: 1.5px;
}

.chip-no-background {
  background: transparent !important;
}

.chip-primary,
.chip-default {
  background-color: var(--utility-gray-50);
  border-color: var(--utility-gray-200);
  color: var(--text-secondary);
}

.chip-primary.chip-outlined,
.chip-default.chip-outlined {
  color: var(--text-secondary);
  border-color: var(--utility-gray-600);
}

.chip-primary.chip-outlined,
.chip-default.chip-outlined {
  color: var(--text-secondary);
  border-color: var(--border-quaternary);
}

.chip-brand {
  background-color: var(--utility-brand-50);
  border-color: var(--utility-brand-200);
  color: var(--text-brand);
}

.chip-brand.chip-outlined {
  color: var(--text-brand);
  border-color: var(--utility-brand-200);
}

.chip-warning {
  background-color: var(--utility-warning-50);
  border-color: var(--utility-warning-200);
  color: var(--text-warning);
}

.chip-warning.chip-outlined {
  color: var(--text-warning);
  border-color: var(--utility-warning-200);
}

.chip-danger,
.chip-error {
  background-color: var(--utility-error-50);
  border-color: var(--utility-error-200);
  color: var(--text-error);
}

.chip-danger.chip-outlined,
.chip-error.chip-outlined {
  color: var(--text-error);
  border-color: var(--utility-error-200);
}

.chip-success {
  background-color: var(--utility-success-50);
  color: var(--text-success);
}

.chip-success.chip-outlined {
  color: var(--text-success);
}

.chip-info {
  background-color: var(--utility-purple-50);
  border-color: var(--utility-purple-200);
  color: var(--text-info);
}

.chip-info.chip-outlined {
  color: var(--text-info);
  border-color: var(--utility-purple-200);
}

/* Label Outlined - Remove Background and Keep Border */
.chip-rounded.chip-outlined {
  border-color: var(--border-primary);
  color: var(--text-secondary);
}

/* Chip Dot */
.chip-outlined.chip-dot {
  border-color: var(--border-primary);
  color: var(--text-primary);
}

.chip-brand.chip-outlined.chip-dot > .icon {
  color: var(--text-brand);
}

.chip-primary.chip-outlined.chip-dot > .icon {
  color: var(--text-primary);
}

.chip-warning.chip-outlined.chip-dot > .icon {
  color: var(--text-warning);
}

.chip-danger.chip-outlined.chip-dot > .icon {
  color: var(--text-error);
}

.chip-success.chip-outlined.chip-dot > .icon {
  color: var(--text-success);
}

.chip-info.chip-outlined.chip-dot > .icon {
  color: var(--text-info);
}

/* Chip Plain */
.chip-plain {
  background-color: transparent;
  border-color: transparent;
  color: var(--text-secondary);
}

.chip-brand.chip-plain {
  color: var(--text-brand);
}

.chip-primary.chip-plain,
.chip-default.chip-plain {
  color: var(--text-secondary);
}

.chip-warning.chip-plain {
  color: var(--text-warning);
}

.chip-danger.chip-plain {
  color: var(--text-error);
}

.chip-success.chip-plain {
  color: var(--text-success);
}

.chip-info.chip-plain {
  color: var(--text-info);
}
</style>
